import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Star, Plus, Sparkles } from "lucide-react";

export default function ReviewImportPanel({ listings = [] }) {
  const [listingId, setListingId] = useState(listings[0]?.id || "");
  const [authorName, setAuthorName] = useState("");
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [reviewDate, setReviewDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (!listingId && listings[0]) setListingId(listings[0].id);
  }, [listings, listingId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!listingId) {
      toast.error("Choisissez une annonce");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews/import", {
        listing_id: listingId,
        author_name: authorName,
        rating,
        comment,
        review_date: reviewDate || null,
      });
      toast.success("Avis Airbnb ajouté");
      setAuthorName("");
      setComment("");
      setReviewDate("");
      setRating(5);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">Migration assistée</div>
        <h1 className="font-display text-4xl font-bold tracking-tight">Importer mes avis Airbnb</h1>
        <p className="text-sm text-neutral-600 mt-3 max-w-2xl leading-relaxed">
          Vous avez accumulé des avis sur Airbnb ? Copiez-les un par un pour les afficher sur votre fiche MSB avec le badge <strong>"Avis Airbnb vérifié"</strong>. Argument de poids pour rassurer les nouveaux voyageurs.
        </p>
      </div>

      <form onSubmit={submit} className="border-2 border-dashed border-[#0A0A0A]/30 p-6 bg-white max-w-2xl">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 bg-[#FF5A5F] text-white flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <div className="font-display text-lg font-bold">Nouvel avis Airbnb</div>
            <p className="text-xs text-neutral-500 mt-0.5">Recopiez exactement le contenu depuis votre tableau de bord Airbnb.</p>
          </div>
        </div>

        <label className="block mb-4">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Annonce</div>
          <select
            data-testid="rev-listing-select"
            value={listingId}
            onChange={(e) => setListingId(e.target.value)}
            className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A] bg-white"
          >
            {listings.map((l) => <option key={l.id} value={l.id}>{l.title}</option>)}
          </select>
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Prénom du voyageur</div>
            <input
              data-testid="rev-author"
              required
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              placeholder="Marie, Paul…"
              className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]"
            />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Date (mois/année)</div>
            <input
              data-testid="rev-date"
              value={reviewDate}
              onChange={(e) => setReviewDate(e.target.value)}
              placeholder="2025-08"
              className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]"
            />
          </label>
        </div>

        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Note</div>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                data-testid={`rev-star-${n}`}
                onClick={() => setRating(n)}
                aria-label={`${n} étoiles`}
              >
                <Star className={`w-7 h-7 ${n <= rating ? "fill-[#0A0A0A] text-[#0A0A0A]" : "text-neutral-300"}`} />
              </button>
            ))}
          </div>
        </div>

        <label className="block mb-5">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Commentaire (collez le texte intégral)</div>
          <textarea
            data-testid="rev-comment"
            required
            rows={5}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Logement superbe, hôte au top, je recommande vivement…"
            className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]"
          />
        </label>

        <button
          data-testid="rev-submit"
          type="submit"
          disabled={submitting || !listings.length}
          className="bg-[#0A0A0A] text-white px-6 py-3 font-semibold hover:bg-[#1B2A49] transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Ajouter l'avis
        </button>

        {!listings.length && (
          <p className="text-xs text-neutral-500 mt-3 italic">
            Créez d'abord une annonce pour pouvoir y attacher des avis.
          </p>
        )}
      </form>
    </>
  );
}
